import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../../lib/api";
import { toast } from "sonner";
import { Loader2, Mail, Info, Zap, PlayCircle } from "lucide-react";
import { useSettings, SectionHeader, SaveBar, TextField, TextArea } from "./shared";
import EmailTemplatesManager from "./EmailTemplatesManager";

const LOG_STATUS = {
  simulated: { label: "Simulé (dev)", cls: "bg-slate-100 text-slate-600" },
  sent: { label: "Envoyé", cls: "bg-emerald-100 text-emerald-700" },
  failed: { label: "Échec", cls: "bg-red-100 text-red-700" },
  skipped_disabled: { label: "Désactivé", cls: "bg-amber-100 text-amber-700" },
};

const TEMPLATE_LABELS = {
  booking_confirmation: "Confirmation de réservation", booking_modified: "Réservation modifiée",
  booking_cancelled: "Réservation annulée", reminder_departure: "Rappel avant départ",
  reminder_return: "Rappel avant retour", quote_send: "Envoi de devis", quote_followup: "Relance de devis",
  contract_available: "Contrat disponible", signature_request: "Demande de signature",
  invoice_available: "Facture disponible", invoice_overdue: "Relance facture impayée",
  payment_receipt: "Reçu de paiement", payment_link: "Lien de paiement",
  review_request: "Demande d'avis", welcome_vendor: "Bienvenue loueur", password_reset: "Réinitialisation mot de passe",
};

const TABS = [
  { key: "sender", label: "Expéditeur" },
  { key: "templates", label: "Modèles" },
  { key: "delays", label: "Rappels & relances" },
  { key: "history", label: "Historique" },
];

export default function EmailsSection() {
  const [tab, setTab] = useState("sender");
  return (
    <div className="max-w-4xl pb-24">
      <SectionHeader
        overline="Emails"
        title="Emails & communication"
        subtitle="Vos emails héritent automatiquement du logo, des couleurs et du thème de votre boutique. Personnalisez les textes, les délais et suivez chaque envoi."
      />
      <div className="flex items-center gap-1 mb-6 border-b border-slate-200" data-testid="emails-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 h-10 text-xs font-bold border-b-2 -mb-px transition-colors ${tab === t.key ? "border-violet-600 text-violet-700" : "border-transparent text-slate-400 hover:text-slate-700"}`}
            data-testid={`emails-tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === "sender" && <SenderPanel />}
      {tab === "templates" && <EmailTemplatesManager />}
      {tab === "delays" && <DelaysPanel />}
      {tab === "history" && <HistoryPanel />}
    </div>
  );
}

function SenderPanel() {
  const { settings, loading, reload } = useSettings();
  const [local, setLocal] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) setLocal({
      sender_name: settings.emails?.sender_name || "",
      reply_to: settings.emails?.reply_to || "",
      signature: settings.emails?.signature || "",
    });
  }, [settings]);

  if (loading || !local) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div>;

  const saved = {
    sender_name: settings.emails?.sender_name || "",
    reply_to: settings.emails?.reply_to || "",
    signature: settings.emails?.signature || "",
  };
  const dirty = JSON.stringify(local) !== JSON.stringify(saved);
  const companyName = settings.company?.name || "";
  const companyEmail = settings.company?.email || "";

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/vendor/settings", { emails: local });
      toast.success("Paramètres d'envoi enregistrés");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setSaving(false);
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 space-y-5" data-testid="emails-sender-section">
      <div className="flex items-start gap-2 rounded-xl border border-violet-100 bg-violet-50/60 px-3.5 py-2.5 text-xs text-slate-700">
        <Info className="h-4 w-4 text-violet-500 shrink-0 mt-0.5" />
        <span>
          Vos emails partent avec l'expéditeur <span className="font-semibold">« {local.sender_name || companyName || "Votre société"} via Junago »</span>.
          La délivrabilité (SPF/DKIM) est gérée par Junago — rien à configurer. Le design (logo, couleurs, boutons) suit automatiquement le thème de votre boutique.
        </span>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <TextField
          label="Nom d'expéditeur" value={local.sender_name}
          onChange={(v) => setLocal((p) => ({ ...p, sender_name: v }))}
          placeholder={companyName || "Nom de votre société"} testid="emails-sender-name"
          hint={`Vide = nom de votre société (${companyName || "à renseigner dans Identité"}).`}
        />
        <TextField
          label="Adresse de réponse (Reply-To)" value={local.reply_to}
          onChange={(v) => setLocal((p) => ({ ...p, reply_to: v }))}
          placeholder={companyEmail || "contact@votresociete.fr"} testid="emails-reply-to"
          hint={`Vide = email de votre société (${companyEmail || "à renseigner dans Identité"}).`}
        />
      </div>
      <TextArea
        label="Signature (ajoutée en bas de chaque email)" value={local.signature} rows={3}
        onChange={(v) => setLocal((p) => ({ ...p, signature: v }))} testid="emails-signature"
        hint="Texte simple, variables autorisées : {vendor_name}. Ex : L'équipe {vendor_name} — 01 23 45 67 89"
      />
      <SaveBar dirty={dirty} saving={saving} onSave={save} testid="emails-save" />
    </div>
  );
}

const DELAY_FIELDS = [
  { key: "reminder_departure_hours", label: "Rappel avant départ", suffix: "heures avant", template: "reminder_departure" },
  { key: "reminder_return_hours", label: "Rappel avant retour", suffix: "heures avant", template: "reminder_return" },
  { key: "quote_followup_days", label: "Relance devis sans réponse", suffix: "jours après envoi", template: "quote_followup" },
  { key: "invoice_followup_days", label: "Relance facture impayée", suffix: "jours après échéance", template: "invoice_overdue" },
  { key: "review_request_days", label: "Demande d'avis", suffix: "jours après retour", template: "review_request" },
];

function DelaysPanel() {
  const { settings, loading, reload } = useSettings();
  const [local, setLocal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => { if (settings) setLocal({ ...settings.emails?.delays }); }, [settings]);
  if (loading || !local) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div>;

  const dirty = JSON.stringify(local) !== JSON.stringify(settings.emails?.delays || {});

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/vendor/settings", { emails: { delays: local } });
      toast.success("Délais enregistrés");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setSaving(false);
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/vendor/emails/automations/run");
      const total = Object.values(data.results).reduce((a, b) => a + b, 0);
      toast.success(total > 0
        ? `${total} email(s) automatique(s) ${data.mode === "live" ? "envoyé(s)" : "simulé(s)"} — voir l'Historique`
        : "Aucun email automatique dû pour le moment");
    } catch { toast.error("Erreur lors de l'exécution"); }
    setRunning(false);
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="emails-delays-section">
      <div className="flex items-start gap-2 rounded-xl border border-amber-100 bg-amber-50/60 px-3.5 py-2.5 text-xs text-slate-700 mb-5">
        <Zap className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
        <span>
          Ces emails partent automatiquement (vérification toutes les 30 minutes, un seul envoi par réservation/devis/facture).
          Chaque modèle peut être désactivé dans l'onglet <span className="font-semibold">Modèles</span>.
          En mode développement, les envois sont simulés et visibles dans l'Historique.
        </span>
      </div>
      <div className="space-y-3">
        {DELAY_FIELDS.map((f) => (
          <div key={f.key} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/50 px-4 py-3">
            <div className="flex-1 text-xs font-semibold text-slate-700">{f.label}</div>
            <input
              type="number" min="1" value={local[f.key] ?? ""}
              onChange={(e) => setLocal((p) => ({ ...p, [f.key]: e.target.value === "" ? "" : Math.max(1, parseInt(e.target.value) || 1) }))}
              className="w-20 h-9 px-2 rounded-lg border border-slate-200 text-sm text-center focus:outline-none focus:ring-2 focus:ring-violet-500/30"
              data-testid={`delay-${f.key}`}
            />
            <div className="text-[11px] text-slate-400 w-32">{f.suffix}</div>
          </div>
        ))}
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button onClick={runNow} disabled={running}
          className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-slate-200 text-slate-600 text-[11px] font-bold hover:bg-slate-50 disabled:opacity-50"
          data-testid="emails-run-automations">
          {running ? <Loader2 className="h-3 w-3 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5" />} Vérifier maintenant
        </button>
        <span className="text-[11px] text-slate-400">Force un passage du planificateur pour votre compte.</span>
      </div>
      <SaveBar dirty={dirty} saving={saving} onSave={save} testid="delays-save" />
    </div>
  );
}

function HistoryPanel() {
  const [log, setLog] = useState(null);
  useEffect(() => {
    api.get("/vendor/emails/log?limit=50").then(({ data }) => setLog(data.emails || [])).catch(() => setLog([]));
  }, []);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="emails-log-section">
      <div className="flex items-center gap-2 mb-4">
        <Mail className="h-4 w-4 text-violet-600" />
        <div className="text-sm font-semibold text-slate-900">Historique des envois</div>
        <span className="text-[10px] text-slate-400">50 derniers</span>
      </div>
      {log === null ? (
        <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-violet-600" /></div>
      ) : log.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 py-8 text-center text-xs text-slate-400">
          Aucun email envoyé pour l'instant. Ils apparaîtront ici (devis, confirmations, contrats, rappels…).
        </div>
      ) : (
        <div className="space-y-1.5">
          {log.map((e) => {
            const st = LOG_STATUS[e.status] || { label: e.status, cls: "bg-slate-100 text-slate-500" };
            return (
              <div key={e.id} className="flex items-center gap-3 px-3 py-2 rounded-xl border border-slate-100 bg-slate-50/50" data-testid={`email-log-${e.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-slate-800 truncate">{e.subject || TEMPLATE_LABELS[e.template_key] || e.template_key}</div>
                  <div className="text-[11px] text-slate-500 truncate">
                    → {e.to} · {TEMPLATE_LABELS[e.template_key] || e.template_key}
                    {e.entity_ref ? ` · ${e.entity_ref}` : ""}
                    {e.scheduled_for ? ` · prévu ${e.scheduled_for}` : ""}
                  </div>
                </div>
                {e.mode && <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold shrink-0 ${e.mode === "live" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>{e.mode === "live" ? "Réel" : "Console"}</span>}
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold shrink-0 ${st.cls}`}>{st.label}</span>
                <span className="text-[10px] text-slate-400 shrink-0">{(e.executed_at || e.created_at || "").slice(0, 16).replace("T", " ")}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
